import { useEffect, useRef, useState, useCallback } from "react";
import { IDataHover } from "../../interface/DataHover";
import { NumericFormat } from "react-number-format";
import { useGetOrderMutation } from "../../features/CallTPApi";
import { IOrder } from "../../interface/IOrder";

interface PopupProps {
  dataHover: IDataHover;
  onClose: () => void;
}

// Вспомогательная функция для проверки клика внутри элемента
const isClickedInside = (event: MouseEvent, element: HTMLElement | null): boolean => {
  if (!element) return false;

  let node: Node | null = event.target as Node;
  while (node) {
    if (node === element) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
};

const Popup = ({ dataHover, onClose }: PopupProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [popupDimensions, setPopupDimensions] = useState({ height: 110, width: 0 });
  const [getOrder, { isLoading }] = useGetOrderMutation();
  const [shouldRender, setShouldRender] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Сохраняем предыдущие данные для плавного перехода
  const [displayData, setDisplayData] = useState<IDataHover>(dataHover);
  const [displayOrderData, setDisplayOrderData] = useState<IOrder | null>(null);

  // Загрузка данных заказа
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!dataHover?.orderId) {
        // Если нет orderId, сразу показываем с новыми данными
        setDisplayOrderData(null);
        setDisplayData(dataHover);
        setShouldRender(true);
        return;
      }

      // Сохраняем базовые данные для немедленного отображения
      setDisplayData(dataHover);
      setShouldRender(true);

      try {
        const response = await getOrder(dataHover.orderId);
        if ('data' in response) {
          setDisplayOrderData(response.data);
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        // Оставляем отображаемые данные как есть, но без информации о заказе
      }
    };

    fetchOrderData();
  }, [dataHover?.orderId, getOrder]);

  // Обновляем displayData при изменении dataHover (для случаев без orderId)
  // useEffect(() => {
  //   console.log("dataHover", dataHover);
  //   if (!dataHover?.orderId) {
  //     setDisplayData(dataHover);
  //   }
  // }, [dataHover]);

  // Обработчик кликов вне попапа
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isClickedInside(event, containerRef.current)) {
        onClose();
      }
    };

    window.addEventListener("click", handleClickOutside);
    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [onClose]);

  // Отслеживаем полный рендер всех элементов
  useEffect(() => {
    if (shouldRender) {
      setIsMounted(true);
    }
  }, [shouldRender]);

  // Расчет размеров попапа после полного рендера
  useEffect(() => {
    if (!isMounted || !containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const { offsetHeight, offsetWidth } = containerRef.current;
        setPopupDimensions({
          height: offsetHeight + 18,
          width: offsetWidth
        });
      }
    };

    // Используем двойной таймаут чтобы гарантировать полный рендер
    const timer1 = setTimeout(() => {
      updateDimensions();

      // Второй пересчет для гарантии
      const timer2 = setTimeout(() => {
        updateDimensions();
      }, 50);

      return () => clearTimeout(timer2);
    }, 0);

    return () => clearTimeout(timer1);
  }, [isMounted, displayData, displayOrderData, isLoading]);

  // Дополнительный пересчет при изменении размеров окна
  useEffect(() => {
    if (!isMounted) return;

    const handleResize = () => {
      if (containerRef.current) {
        const { offsetHeight, offsetWidth } = containerRef.current;
        setPopupDimensions({
          height: offsetHeight + 18,
          width: offsetWidth
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMounted]);

  // Определяем отступ в зависимости от наличия orderId
  const getVerticalOffset = useCallback(() => {
    if (!dataHover.category) {
      return 65
    }
    if (dataHover.groupTag && displayData.orderId) {
      return 160
    }
    return displayData.orderId ? 133 : 110;
  }, [displayData.orderId]);

  // Позиционирование попапа по центру
  const getPopupPosition = useCallback(() => {
    if (!displayData.pos || !shouldRender || popupDimensions.width === 0) {
      return {
        top: '0px',
        left: '0px',
        visibility: 'hidden' as const
      };
    }

    const verticalOffset = getVerticalOffset();

    return {
      top: `${displayData.pos.y - verticalOffset}px`,
      left: `${displayData.pos.x - popupDimensions.width / 2}px`,
      visibility: 'visible' as const
    };
  }, [displayData.pos, popupDimensions, shouldRender, getVerticalOffset]);

  // Рендер контента в зависимости от типа данных
  const renderContent = () => {
    const { groupTag, row, place, category, quantitySeat, categoryFill } = displayData;
    const getSeatDeclension = (count: any): string => {
      const lastDigit = count % 10;
      const lastTwoDigits = count % 100;

      // Исключения для 11-14
      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return 'мест';
      }

      // Правила склонения
      if (lastDigit === 1) {
        return 'место';
      } else if (lastDigit >= 2 && lastDigit <= 4) {
        return 'места';
      } else {
        return 'мест';
      }
    };
    // Отдельный элемент
    if (groupTag == null) {
      return (
        <>
          <div style={{ color: 'white', textWrap: 'nowrap' }} className="text-nowrap">
            {row} {place && <> Место: {place}</>}
          </div>
          {category && (
            <div style={{ color: categoryFill ?? '#000000' }}>
              {category}
            </div>
          )}
        </>
      );
    }

    // Группа элементов
    return (
      <>
        <div style={{ color: categoryFill ?? "#000000", textWrap: 'nowrap' }}>
          {category}
        </div>
        <div className="text-nowrap" style={{ color: 'white', textWrap: 'nowrap' }}>
          {row}, {quantitySeat} {getSeatDeclension(quantitySeat)}
        </div>
      </>
    );
  };

  // Рендер информации о заказе
  const renderOrderInfo = () => {
    if (!displayData.orderId) return null;

    if (isLoading) {
      return <div style={{ color: 'white' }}>Загрузка...</div>;
    }

    return (
      <div style={{ color: 'white', textWrap: 'nowrap' }}>
        <div>Заказ: {displayOrderData?.orderNumber}</div>
        {/* <div>Статус: {displayOrderData && <OrderStatusString status={displayOrderData.orderStatus} />}</div> */}
      </div>
    );
  };

  // Рендер цены
  const renderPrice = () => {
    if (!displayData.price || displayData.price <= 0) return null;

    return (
      <div style={{ color: 'white', textWrap: 'nowrap' }}>
        <NumericFormat
          displayType={'text'}
          value={displayData.price}
          allowLeadingZeros
          thousandSeparator=" "
        /> ₽
      </div>
    );
  };

  // Рендер стрелочки
  const renderArrow = () => {
    return (
      <div
        style={{
          position: "absolute",
          bottom: "-10px",
          left: "50%",
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "10px solid transparent",
          borderRight: "10px solid transparent",
          borderTop: "10px solid #000",
        }}
      />
    );
  };

  // Не рендерим попап пока данные не загружены
  if (!shouldRender) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="popup-seat-hover"
      style={{
        position: "absolute",
        ...getPopupPosition(),
        padding: "10px",
        borderRadius: "3px",
        boxShadow: "0 0 10px black",
        zIndex: 1500,
        backgroundColor: "#000",
        textAlign: "center",
        // transition: 'all 0.3s ease',
        minWidth: "150px",
        opacity: isLoading ? 0.7 : 1,
      }}
    >
      {renderContent()}
      {renderOrderInfo()}
      {renderPrice()}
      {renderArrow()}
    </div>
  );
};

export default Popup;